/**
 * Contacts API Mutations
 *
 * Create, update and delete operations.
 */

// API client
import { apiClient } from '../client';

// Types
import type {
  CreateContactPayload,
  CreateBillingAddressPayload,
  UpdateContactPayload,
  ApiContact,
  ContactShowResponse,
} from './types';

// Create (GRAIN-1914/1915): wraps the payload under the `contact` key to match
// the controller's `create_params`. 422s carry field-level errors in
// ApiError.fieldErrors (see ../client.ts).
export async function createContact(payload: CreateContactPayload): Promise<ApiContact> {
  const response = await apiClient.post<ContactShowResponse>('/api/contacts', {
    contact: payload,
  });
  return response.data.contact;
}

// Update (GRAIN-1916/1917): wraps the payload under the `contact` key and returns
// the full ApiContact (the endpoint renders ContactSerializer#to_detail_h). 422s
// carry field-level errors in ApiError.fieldErrors.
export async function updateContact(
  id: number,
  payload: UpdateContactPayload
): Promise<ApiContact> {
  const response = await apiClient.patch<ContactShowResponse>(`/api/contacts/${id}`, {
    contact: payload,
  });
  return response.data.contact;
}

// Soft-deletes a contact (Discard gem, server-side). Idempotent: the endpoint
// returns 204 even for an unknown or already-discarded id (GRAIN-1956), so a
// re-delete resolves rather than throwing.
export async function deleteContact(id: number): Promise<void> {
  await apiClient.delete<void>(`/api/contacts/${id}`);
}

// ─── Billing addresses (GRAIN-1906) ──────────────────────────────────────────
// Each endpoint returns the full updated contact so the detail page re-renders
// its list from one response. 422s carry field errors in ApiError.fieldErrors.

export async function createBillingAddress(
  contactId: number,
  payload: CreateBillingAddressPayload
): Promise<ApiContact> {
  const response = await apiClient.post<ContactShowResponse>(
    `/api/contacts/${contactId}/billing_addresses`,
    { billing_address: payload }
  );
  return response.data.contact;
}

export async function updateBillingAddress(
  contactId: number,
  id: number,
  payload: CreateBillingAddressPayload
): Promise<ApiContact> {
  const response = await apiClient.patch<ContactShowResponse>(
    `/api/contacts/${contactId}/billing_addresses/${id}`,
    { billing_address: payload }
  );
  return response.data.contact;
}

export async function deleteBillingAddress(contactId: number, id: number): Promise<ApiContact> {
  const response = await apiClient.delete<ContactShowResponse>(
    `/api/contacts/${contactId}/billing_addresses/${id}`
  );
  return response.data.contact;
}

export async function setDefaultBillingAddress(contactId: number, id: number): Promise<ApiContact> {
  const response = await apiClient.patch<ContactShowResponse>(
    `/api/contacts/${contactId}/billing_addresses/${id}/set_default`,
    {}
  );
  return response.data.contact;
}
